import React, { PropsWithChildren, useContext, useEffect, useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useLossPercentagesReadContext } from '@context/LossPercentagesContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSignup } from '@context/SignupContext'

import { Heading6 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { requireDepositBeforeKYC } from '@utils/AccountAccess/accountKYCStatuses'
import { CommonPaths } from '@utils/path'

import { ActionModal } from '@shared/modals/Action.modal'
import { RiskWarningModal } from '@shared/modals/RiskWarning.modal'

import { useTraderRoomRoutesConditions } from '../../Traders-Room/TradersRoom.routes'
import { useProducts } from '../hooks/useProducts'

import styles from './SignupFormWrapper.module.scss'

interface SignupFormWrapperProps {
  label: string
  onBackToPreviousStep?(): void
  cancelPath?: string
  hideBackArrow?: boolean
  hideCloseIcon?: boolean
  id?: string
}

export const SignupFormWrapper: React.FC<PropsWithChildren<SignupFormWrapperProps>> = ({
  label,
  children,
  hideBackArrow,
  hideCloseIcon,
  id,
  ...props
}) => {
  const { resetSignup } = useSignup()
  const navigate = useNavigate()
  const [auth] = useContext(AuthSessionContext)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()

  const redirectLink = isTradersRoomVisible
    ? CommonPaths.TradersRoomWallets
    : CommonPaths.IntroducingBrokerWallets

  const [closeIconVisible, setCloseIconVisible] = useState(false)
  const { selectedProductIds } = useProducts()

  const { percentages } = useLossPercentagesReadContext()

  const riskWarningModal = useModal(RiskWarningModal, {
    selectedProductIds: selectedProductIds,
    risk: percentages?.percentage,
  })

  useEffect(() => {
    if (hideCloseIcon) {
      setCloseIconVisible(false)
      return
    }
    if (props.onBackToPreviousStep) {
      setCloseIconVisible(false)
    } else {
      setCloseIconVisible(true)
    }
  }, [props.onBackToPreviousStep, hideCloseIcon])

  const handleCancelAction = () => {
    resetSignup()
    if (!auth) {
      navigate('/login')
    } else {
      refreshAccount(locale)
      if (requireDepositBeforeKYC(account)) {
        return navigate(props.cancelPath || CommonPaths.FirstTimeDeposit)
      }
      navigate(props.cancelPath || redirectLink)
    }
  }

  const confirmModal = useModal(ActionModal, { onConfirm: handleCancelAction })

  return (
    <React.Fragment>
      <div id={id} className={styles.wrapper}>
        <div className={styles.innerWrapper}>
          {props.onBackToPreviousStep && !hideBackArrow && (
            <IconButton.Ghost
              id='signup_backarrow'
              size='md'
              icon={ArrowIcon.Left}
              onClick={props.onBackToPreviousStep}
            />
          )}
          {closeIconVisible && (
            <IconButton.Ghost size='md' icon={CloseIcon} onClick={() => confirmModal.open()} />
          )}
          <Heading6 className={styles.title} medium>
            {label}
          </Heading6>
          <IconButton.Ghost
            size='md'
            icon={{ icon: WarningIcon, color: 'text-warning-high-em' }}
            onClick={() => riskWarningModal.open()}
          />
        </div>
        {children}
      </div>
    </React.Fragment>
  )
}
